import React, { FC } from 'react';
import Skeleton from '@material-ui/lab/Skeleton';
import { Box, Checkbox, Link, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow } from '@material-ui/core';
import { TableCellMiddle, TableCellEnd, TableCellStart } from 'components';
import TableRowCustom from 'components/Table/TableRowCustom';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import NumberFormat from 'components/NumberFormat';
import { format } from 'date-fns';
import { Alert, AlertTitle } from '@material-ui/lab';

interface Props {
  index: number;
  invoice: InvoiceModel;
  isLoading: boolean;
  isUpdate: boolean;
  totalPay: number;
  handleIndividualCheck: (item: { InvoiceId: number }) => void;
  otherPayment?: OtherPayment;
  payload?: PayloadPayment | null;
  hasPending?: boolean;
}

const BodyRow: FC<Props> = props => {
  const { invoice, isLoading, isUpdate, totalPay, payload, otherPayment, hasPending, handleIndividualCheck } = props;
  const handleSetId = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    handleIndividualCheck({ InvoiceId: invoice.id });
  };

  const handleSisaTagihan = () => {
    const totalPlan = (otherPayment?.data ?? []).reduce((acc, curr) => acc + curr.planTotalPay, 0);
    return invoice.totalPrice - (invoice.totalPay + totalPlan);
  };

  const handlePayloadInvoice = () => {
    if (!isUpdate) return 0;
    let total = 0;

    if (payload && payload.InvoiceId) {
      const find = payload.InvoiceId.find(f => f.id === invoice.id);
      if (find) {
        total = find.totalPay;
      }
    }

    return total;
  };

  const existInPayload = () => {
    let ex = false;
    if (payload && payload.InvoiceId) {
      const find = payload.InvoiceId.find(f => f.id === invoice.id);
      if (find) {
        ex = true;
      }
    }

    return ex;
  };

  return (
    <>
      <TableRowCustom selected={hasPending}>
        <TableCellStart>
          {isLoading ? (
            <Skeleton variant='text' width={'100%'} height={25} />
          ) : (
            <Checkbox
              key={invoice.id}
              icon={<CheckBoxOutlineBlankIcon />}
              checkedIcon={<CheckBoxIcon />}
              edge='start'
              color='primary'
              checked={true}
              tabIndex={-1}
              disableRipple
              onChange={handleSetId}
            />
          )}
        </TableCellStart>

        <TableCellMiddle align='center'>{isLoading ? <Skeleton variant='text' width={'100%'} height={25} /> : invoice.invoiceNumber}</TableCellMiddle>

        <TableCellMiddle align='center'>
          {isLoading ? (
            <Skeleton variant='text' width={'100%'} height={25} />
          ) : invoice.orderDate ? (
            format(new Date(invoice.orderDate), 'dd/MM/yyyy')
          ) : (
            ''
          )}
        </TableCellMiddle>

        <TableCellMiddle align='right'>
          {isLoading ? (
            <Skeleton variant='text' width={'100%'} height={25} />
          ) : (
            <NumberFormat value={invoice.totalPrice} prefix={'Rp'} thousandSeparator={true} displayType='text' />
          )}
        </TableCellMiddle>

        <TableCellEnd align='right'>
          {isLoading ? (
            <Skeleton variant='text' width={'100%'} height={25} />
          ) : (
            <NumberFormat value={invoice.totalPrice - invoice.totalPay} prefix={'Rp'} thousandSeparator={true} displayType='text' />
          )}
        </TableCellEnd>
      </TableRowCustom>

      {otherPayment && otherPayment.invoiceId === invoice.id ? (
        <TableRow>
          <TableCell colSpan={5} padding='none' style={{ backgroundColor: '#f4f4f4' }}>
            <Box style={{ padding: '8px', marginBottom: '8px' }}>
              <Alert severity='warning' variant='outlined'>
                <AlertTitle>Mohon Diperhatikan</AlertTitle>
                Terdapat beberapa pembayaran untuk invoice ini yang statusnya masih pending dan belum di-approve.
                <br /> Jumlah pembayaran ini harus sesuai dengan total nilai invoice, karena pembayaran yang masih{' '}
                <code style={{ color: 'red' }}>Pending/Menunggu Autorisasi</code> belum mengurangi total tagihan.
              </Alert>
            </Box>

            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell size='small' component={'th'} align='center'>
                      Nomor Pembayaran
                    </TableCell>
                    <TableCell size='small' component={'th'} align='center'>
                      Metode Pembayaran
                    </TableCell>
                    <TableCell size='small' component={'th'} align='center'>
                      Dibuat Tanggal
                    </TableCell>
                    <TableCell size='small' component={'th'} align='center'>
                      Autorisasi
                    </TableCell>
                    <TableCell size='small' component={'th'} align='center'>
                      Status
                    </TableCell>
                    <TableCell size='small' component={'th'} align='center'>
                      Total Bayar
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {(otherPayment?.data ?? []).map(val => (
                    <TableRow key={val.id}>
                      <TableCell size='small'>
                        <Link href={`/pembayaran-penjualan/${val.id}`} target='_blank' underline='none'>
                          {val.paymentNumber}
                        </Link>
                      </TableCell>
                      <TableCell size='small' align='center'>
                        {val.paymentMethod.replaceAll('_', ' ')}
                      </TableCell>
                      <TableCell size='small' align='center'>
                        {new Date(val.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell size='small' align='center'>
                        Menunggu Autorisasi
                      </TableCell>
                      <TableCell size='small' align='center'>
                        {val.status === 'PAID' ? 'Dibayarkan' : 'Belum Dibayarkan'}
                      </TableCell>
                      <TableCell size='small' align='right'>
                        -<NumberFormat value={val.planTotalPay} prefix={'Rp'} thousandSeparator={true} displayType='text' />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell size='small' colSpan={5} align='right'>
                      Rencana Sisa Tagihan
                    </TableCell>
                    <TableCell size='small' align='right'>
                      <NumberFormat value={handleSisaTagihan()} prefix={'Rp'} thousandSeparator={true} displayType='text' />
                    </TableCell>
                  </TableRow>
                </TableBody>

                {isUpdate && existInPayload() ? (
                  <TableRow>
                    <TableCell size='small' colSpan={5} align='right'>
                      Pembayaran yang berlaku saat ini
                    </TableCell>
                    <TableCell size='small' align='right'>
                      -<NumberFormat value={handlePayloadInvoice()} prefix={'Rp'} thousandSeparator={true} displayType='text' />
                    </TableCell>
                  </TableRow>
                ) : null}

                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={6} align='center'>
                      <code style={{ color: 'red' }}>Ini adalah jumlah rencana pembayaran yang akan dibayarkan</code>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
};

export default BodyRow;
